import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "../../libs/axios";
import { store } from "../../store/useStore";

type TModuleType =
  | "lead"
  | "application"
  | "agent"
  | "bank_account"
  | "gic_account"
  | "block_account"
  | "accommodation"
  | "test_preparation"
  | "education_loan"
  | "forex_card"
  | "sim_card"
  | "voucher"
  | "forex_transfer"
  | "insurance";

export function useSingleForm({
  page,
  per_page,
  params,
  enabled,
}: {
  page?: number;
  per_page?: number;
  params?: {
    [key: string]: any;
    module_type?: TModuleType;
  };
  enabled?: boolean;
}) {
  const active_device = store.auth.activeDeviceIdentifier();

  return useQuery({
    queryKey: ["single-form", params?.module_type],
    queryFn: async () => {
      const { data } = await axios.get(`/public/forms/${params?.module_type}`, {
        params: {
          page,
          per_page,
          ...params,
        },
        headers: {
          "fi-device-number": active_device,
        },
      });
      return data.data;
    },
    refetchOnWindowFocus: false,
    enabled,
  });
}

export function useGetServiceList({
  page,
  per_page,
  enabled,
  params,
  module_type,
}: {
  page?: number;
  per_page?: number;
  enabled?: boolean;
  module_type: TModuleType;
  params?: any;
}) {
  const active_device = store.auth.activeDeviceIdentifier();

  return useQuery({
    queryKey: ["service-list", module_type, params, page, per_page],
    queryFn: async () => {
      const { data } = await axios.get("/services", {
        params: {
          page,
          per_page,
          module_type,
          ...params,
        },
        headers: {
          "fi-device-number": active_device,
        },
      });
      return data.data;
    },
    refetchOnWindowFocus: false,
    enabled,
  });
}

export function useGetServiceTimeline({
  module_id,
  module_type,
}: {
  module_id: string;
  module_type?: TModuleType;
}) {
  const active_device = store.auth.activeDeviceIdentifier();
  return useQuery({
    queryKey: ["service-timeline", module_id, module_type],
    queryFn: async () => {
      const { data } = await axios.get(`/services/timeline`, {
        params: {
          module_id,
          module_type,
        },
        headers: {
          "fi-device-number": active_device,
        },
      });
      return data.data;
    },
  });
}

export function useCreateServicesForm({
  isPublicEndpoint,
}: {
  isPublicEndpoint?: boolean;
}) {
  const active_device = store.auth.activeDeviceIdentifier();

  return useMutation({
    mutationFn: async ({ data }: { data: any }) => {
      const { data: response } = await axios.post(
        isPublicEndpoint ? "/public/forms/create" : `/service-form/create`,
        data,
        {
          headers: {
            "fi-device-number": active_device,
          },
        }
      );
      return response;
    },
  });
}

export function useUpdateServicesForm() {
  const active_device = store.auth.activeDeviceIdentifier();

  return useMutation({
    mutationFn: async ({
      data,
      module_lead_id,
    }: {
      data: any;
      module_lead_id: string;
    }) => {
      const { data: response } = await axios.put(
        `/service-form/edit/${module_lead_id}`,
        data,
        {
          headers: {
            "fi-device-number": active_device,
          },
        }
      );
      return response;
    },
  });
}
